import { createContext, useContext, useEffect, useMemo, useReducer } from "react";
import loginService from "../telas/login/loginService";
import {
    buildClientTheme,
    DEFAULT_CLIENT_THEME,
    getStoredClientTheme,
    storeClientTheme,
} from "./clientTheme";

const ThemeContext = createContext();

const getInitialThemeMode = () => window.localStorage.getItem("theme") || "light";
const hasAuthenticatedSession = () => Boolean(sessionStorage.getItem("token"));

const getInitialState = () => ({
    theme: getInitialThemeMode(),
    clientTheme: getStoredClientTheme(),
    isReady: !hasAuthenticatedSession(),
});

const reducer = (state, action) => {
    switch (action.type) {
        case "toggleThemeMode":
            return { ...state, theme: state.theme === "light" ? "dark" : "light" };
        case "applyClientTheme":
            return { ...state, clientTheme: action.data };
        case "resetClientTheme":
            return { ...state, clientTheme: { ...DEFAULT_CLIENT_THEME } };
        case "setReady":
            return { ...state, isReady: action.data };
        default:
            return state;
    }
};

export const ThemeProvider = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, undefined, getInitialState);
    const { theme, clientTheme, isReady } = state;

    useEffect(() => {
        if (window.localStorage.getItem("theme") !== theme) {
            window.localStorage.setItem("theme", theme);
        }
    }, [theme]);

    useEffect(() => {
        document.documentElement.style.setProperty("--client-primary-color", clientTheme.primaryColor);
        document.documentElement.style.setProperty("--client-primary-color-dark", clientTheme.primaryColorDark);
        storeClientTheme(clientTheme);
    }, [clientTheme]);

    useEffect(() => {
        if (!hasAuthenticatedSession()) {
            dispatch({ type: "setReady", data: true });
            return undefined;
        }

        const storedUserId = sessionStorage.getItem("userId");
        if (storedUserId && sessionStorage.getItem("clientTheme")) {
            dispatch({ type: "setReady", data: true });
            return undefined;
        }

        let isMounted = true;
        dispatch({ type: "setReady", data: false });

        loginService
            .getAuthenticatedUser()
            .then((response) => {
                if (!isMounted) {
                    return;
                }

                dispatch({
                    type: "applyClientTheme",
                    data: buildClientTheme(response.data),
                });
            })
            .catch(() => {
                if (isMounted) {
                    dispatch({ type: "resetClientTheme" });
                }
            })
            .finally(() => {
                if (isMounted) {
                    dispatch({ type: "setReady", data: true });
                }
            });

        return () => {
            isMounted = false;
        };
    }, []);

    const value = useMemo(() => ({
        theme,
        clientTheme,
        isReady,
        toggle: () => dispatch({ type: "toggleThemeMode" }),
        applyAuthenticatedTheme: (user) => dispatch({ type: "applyClientTheme", data: buildClientTheme(user) }),
        resetClientTheme: () => dispatch({ type: "resetClientTheme" }),
    }), [clientTheme, isReady, theme]);

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => useContext(ThemeContext);